/**
 * SOLOMON — DISPOSITION LOGGER
 * Tier 1 cold-call workflow. Renders inside LeadDetail above the calculator.
 * One-shot disposition write to /api/calls/log; state transition (if any)
 * runs on the backend through leadStateService.
 *
 * Disposition → state map (server-authoritative, mirrored here only for hints):
 *   no_answer / disconnected / wrong_number   — no state change
 *   voicemail_left                            — new → contacted
 *   connected_not_interested                  — * → dead
 *   connected_interested                      — * → responded_warm
 *   dnc_requested                             — * → opted_out (sets tcpa_dnc=TRUE)
 */

import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { STATE_LABELS } from './leadsConstants';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const DISPOSITIONS = [
  { value: 'no_answer',                label: 'No answer',                hint: 'No state change' },
  { value: 'voicemail_left',           label: 'Voicemail left',           hint: 'state → contacted' },
  { value: 'connected_not_interested', label: 'Connected — not interested', hint: 'state → dead' },
  { value: 'connected_interested',     label: 'Connected — interested',   hint: 'state → responded warm' },
  { value: 'connected_listed_with_agent', label: 'Connected — listed with agent', hint: 'state → contacted · auto-callback ~120d' },
  { value: 'dnc_requested',            label: 'DNC requested',            hint: 'opt-out + tcpa_dnc set' },
  { value: 'wrong_number',             label: 'Wrong number',             hint: 'no state change — update phone manually' },
  { value: 'disconnected',             label: 'Disconnected',             hint: 'no state change' },
];

export default function DispositionLogger({ leadId, lead, onLogged }) {
  const { authFetch } = useAuth();
  const [disposition, setDisposition] = useState('no_answer');
  const [notes, setNotes]             = useState('');
  const [callbackDueAt, setCallback]  = useState('');
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState(null);
  const [warn, setWarn]               = useState(null);
  const [success, setSuccess]         = useState(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    setWarn(null);
    setSuccess(null);
    try {
      const r = await authFetch(`${API_BASE}/api/calls/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          leadId,
          disposition,
          notes: notes.trim() || null,
          callbackDueAt: callbackDueAt || null,
        }),
      });
      const json = await r.json();
      if (!r.ok || !json.success) throw new Error(json.error || 'Failed to log call');

      const { transition, transitionError } = json.data;
      if (transition) {
        const from = STATE_LABELS[transition.fromState] || transition.fromState;
        const to   = STATE_LABELS[transition.toState]   || transition.toState;
        setSuccess(`Logged. State ${from} → ${to}.`);
      } else if (transitionError) {
        // The call attempt is saved, but the mapped state change couldn't
        // apply. Surface it — a swallowed transition otherwise looks
        // identical to a clean log ("Logged.") and reads as "nothing happened".
        const already = /already in state/i.test(transitionError);
        setWarn(
          already
            ? 'Call logged — the lead is already in that state, so no change was made.'
            : `Call logged, but the state couldn't be updated: ${transitionError}`
        );
      } else {
        setSuccess('Logged.');
      }
      setNotes('');
      setCallback('');
      setDisposition('no_answer');
      onLogged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const phone = lead?.phone_primary || '—';

  return (
    <section className="lead-detail-section disposition-logger">
      <div className="dl-header">
        <h3 className="lead-detail-h">Log Call</h3>
        <div className="dl-phone">
          <span className="dl-phone-label">Dial</span>
          <span className="dl-phone-num">{phone}</span>
        </div>
      </div>

      <div className="dl-grid">
        <label className="dl-field">
          <span className="dl-field-label">Disposition</span>
          <select
            className="dl-input"
            value={disposition}
            onChange={(e) => setDisposition(e.target.value)}
            disabled={saving}
          >
            {DISPOSITIONS.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
          <span className="dl-field-hint">
            {DISPOSITIONS.find((d) => d.value === disposition)?.hint}
          </span>
        </label>

        <label className="dl-field">
          <span className="dl-field-label">Callback due (optional)</span>
          <input
            type="datetime-local"
            className="dl-input"
            value={callbackDueAt}
            onChange={(e) => setCallback(e.target.value)}
            disabled={saving}
          />
          <span className="dl-field-hint">
            Surfaces in tomorrow's morning briefing if set.
          </span>
        </label>
      </div>

      <label className="dl-field dl-field--full">
        <span className="dl-field-label">Notes</span>
        <textarea
          className="dl-textarea"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="What did the seller say? Conditions, motivation, follow-up."
          rows={3}
          disabled={saving}
        />
      </label>

      {error && <div className="dl-error">{error}</div>}
      {warn && <div className="dl-warn">{warn}</div>}
      {success && <div className="dl-success">{success}</div>}

      <div className="dl-actions">
        <button className="dl-submit" onClick={submit} disabled={saving}>
          {saving ? 'Logging…' : 'Log call'}
        </button>
      </div>
    </section>
  );
}
